import React, { useState } from 'react';
import { CheckCircle2, Pencil, Pin, Trash2 } from 'lucide-react';
import { PosNote } from '../../types';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { useAuth } from '../../contexts/AuthContext';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import { formatDateTime, hasRealDate } from '../../utils/datetime';
import { usePosNotes } from './usePosNotes';

interface PosNotesSectionProps {
  posId: string;
}

type KindLabel = 'PoS' | 'Visit' | 'Task';

function kindLabel(kind: PosNote['kind']): KindLabel {
  if (kind === 'visit') return 'Visit';
  if (kind === 'task') return 'Task';
  return 'PoS';
}

function canEditNote(
  note: PosNote,
  userId: string | undefined,
  isAdmin: boolean
): boolean {
  if (!userId) return false;
  if (isAdmin) return true;
  return note.user_id === userId;
}

function KindBadge({ kind }: { kind: PosNote['kind'] }) {
  const label = kindLabel(kind);
  const styles =
    kind === 'task'
      ? 'bg-amber-100 text-amber-800'
      : kind === 'visit'
        ? 'bg-blue-100 text-blue-800'
        : 'bg-gray-200 text-gray-700';
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-bold ${styles}`}>
      {label}
    </span>
  );
}

interface NoteMetaProps {
  note: PosNote;
  isCompletedTask: boolean;
}

function NoteMeta({ note, isCompletedTask }: NoteMetaProps) {
  const author = note.user_name || 'Unknown';
  const created = hasRealDate(note.created_at)
    ? formatDateTime(note.created_at)
    : '';
  const resolved =
    isCompletedTask && hasRealDate(note.task_resolved_at)
      ? formatDateTime(note.task_resolved_at)
      : null;

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
      <span className="font-bold text-gray-700">{author}</span>
      {created && <span>{created}</span>}
      <KindBadge kind={note.kind} />
      {note.edited_at && (
        <span className="italic text-gray-400">edited</span>
      )}
      {isCompletedTask && (
        <span className="inline-flex items-center gap-1 text-green-700">
          <CheckCircle2 size={12} />
          Done{resolved ? ` · ${resolved}` : ''}
        </span>
      )}
    </div>
  );
}

interface NoteActionsProps {
  onEdit: () => void;
  onDelete: () => void;
  busy: boolean;
}

function NoteActions({ onEdit, onDelete, busy }: NoteActionsProps) {
  return (
    <div className="flex items-center gap-1 flex-shrink-0">
      <button
        type="button"
        onClick={onEdit}
        disabled={busy}
        aria-label="Edit note"
        className="min-h-[44px] min-w-[44px] inline-flex items-center
          justify-center rounded-lg text-gray-500 hover:bg-gray-100
          hover:text-gray-800 disabled:opacity-50"
      >
        <Pencil size={16} />
      </button>
      <button
        type="button"
        onClick={onDelete}
        disabled={busy}
        aria-label="Delete note"
        className="min-h-[44px] min-w-[44px] inline-flex items-center
          justify-center rounded-lg text-gray-500 hover:bg-red-50
          hover:text-red-600 disabled:opacity-50"
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
}

interface NoteEditFormProps {
  initialBody: string;
  initialPinned: boolean;
  busy: boolean;
  onCancel: () => void;
  onSave: (body: string, isPinned: boolean) => void;
}

function NoteEditForm({
  initialBody,
  initialPinned,
  busy,
  onCancel,
  onSave,
}: NoteEditFormProps) {
  const [body, setBody] = useState(initialBody);
  const [isPinned, setIsPinned] = useState(initialPinned);
  const trimmed = body.trim();

  return (
    <div className="space-y-3">
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={3}
        disabled={busy}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base
          outline-none resize-none focus:ring-2 focus:ring-brand-500"
      />
      <label className="flex items-center gap-2 text-sm text-gray-700 select-none">
        <input
          type="checkbox"
          checked={isPinned}
          disabled={busy}
          onChange={(e) => setIsPinned(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-brand-600
            focus:ring-brand-500"
        />
        Pin as permanent PoS info
      </label>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="min-h-[44px] px-4 py-2 rounded-lg border border-gray-300
            text-sm font-bold text-gray-700 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={busy || trimmed.length === 0}
          onClick={() => onSave(trimmed, isPinned)}
          className="min-h-[44px] px-4 py-2 rounded-lg bg-brand-600 text-white
            text-sm font-bold hover:bg-brand-700 disabled:opacity-50"
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

interface NoteCardProps {
  note: PosNote;
  canEdit: boolean;
  busy: boolean;
  isEditing: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: (body: string, isPinned: boolean) => void;
  onDelete: () => void;
}

function NoteCard({
  note,
  canEdit,
  busy,
  isEditing,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
}: NoteCardProps) {
  const isCompletedTask =
    note.kind === 'task' && note.task_status === 'completed';
  const muted = isCompletedTask ? 'opacity-70 bg-gray-50/80' : 'bg-white';

  return (
    <div className={`rounded-xl border border-gray-100 p-3 space-y-2 ${muted}`}>
      <div className="flex items-start justify-between gap-2">
        <NoteMeta note={note} isCompletedTask={isCompletedTask} />
        {canEdit && !isEditing && (
          <NoteActions onEdit={onStartEdit} onDelete={onDelete} busy={busy} />
        )}
      </div>
      {isEditing ? (
        <NoteEditForm
          initialBody={note.body}
          initialPinned={note.is_pinned}
          busy={busy}
          onCancel={onCancelEdit}
          onSave={onSaveEdit}
        />
      ) : (
        <p
          className={`text-sm whitespace-pre-wrap ${
            isCompletedTask ? 'text-gray-500 line-through' : 'text-gray-700'
          }`}
        >
          {note.body}
        </p>
      )}
    </div>
  );
}

interface AddNoteFormProps {
  isOnline: boolean;
  submitting: boolean;
  onSubmit: (body: string, isPinned: boolean) => Promise<boolean>;
}

function AddNoteForm({ isOnline, submitting, onSubmit }: AddNoteFormProps) {
  const [body, setBody] = useState('');
  const [isPinned, setIsPinned] = useState(false);
  const trimmed = body.trim();
  const disabled = !isOnline || submitting;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!trimmed || disabled) return;
    const ok = await onSubmit(trimmed, isPinned);
    if (ok) {
      setBody('');
      setIsPinned(false);
    }
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="space-y-3">
      <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
        Add note
      </label>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={3}
        disabled={disabled}
        placeholder={
          isOnline ? 'Write a note about this PoS…' : 'Notes require a connection'
        }
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base
          outline-none resize-none focus:ring-2 focus:ring-brand-500
          disabled:bg-gray-100 disabled:text-gray-400"
      />
      <label
        className={`flex items-center gap-2 text-sm select-none ${
          disabled ? 'text-gray-400' : 'text-gray-700 cursor-pointer'
        }`}
      >
        <input
          type="checkbox"
          checked={isPinned}
          disabled={disabled || trimmed.length === 0}
          onChange={(e) => setIsPinned(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-brand-600
            focus:ring-brand-500 disabled:opacity-50"
        />
        Pin as permanent PoS info
      </label>
      {!isOnline && (
        <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
          Notes need an internet connection. Reconnect to add or edit notes.
        </p>
      )}
      <button
        type="submit"
        disabled={disabled || trimmed.length === 0}
        className="min-h-[44px] w-full sm:w-auto px-5 py-2 rounded-xl
          bg-brand-600 text-white text-sm font-bold hover:bg-brand-700
          disabled:opacity-50"
      >
        {submitting ? 'Saving…' : 'Add note'}
      </button>
    </form>
  );
}

function NotesLoading() {
  return (
    <div className="bg-gray-50 p-4 rounded-xl mt-6 animate-pulse space-y-3">
      <div className="h-5 bg-gray-200 rounded w-24" />
      <div className="h-16 bg-gray-200 rounded-xl" />
      <div className="h-16 bg-gray-200 rounded-xl" />
    </div>
  );
}

interface NoteListProps {
  notes: PosNote[];
  userId: string | undefined;
  isAdmin: boolean;
  busyId: string | null;
  editingId: string | null;
  onStartEdit: (id: string) => void;
  onCancelEdit: () => void;
  onSaveEdit: (id: string, body: string, pin: boolean) => void;
  onDelete: (id: string) => void;
}

function NoteList({
  notes,
  userId,
  isAdmin,
  busyId,
  editingId,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
}: NoteListProps) {
  return (
    <div className="space-y-2">
      {notes.map((note) => (
        <NoteCard
          key={note.id}
          note={note}
          canEdit={canEditNote(note, userId, isAdmin)}
          busy={busyId === note.id}
          isEditing={editingId === note.id}
          onStartEdit={() => onStartEdit(note.id)}
          onCancelEdit={onCancelEdit}
          onSaveEdit={(body, pin) => onSaveEdit(note.id, body, pin)}
          onDelete={() => onDelete(note.id)}
        />
      ))}
    </div>
  );
}

function TimelineEmpty({ hasPinned }: { hasPinned: boolean }) {
  if (!hasPinned) {
    return <p className="text-sm text-gray-500 py-2">No notes yet.</p>;
  }
  return (
    <p className="text-sm text-gray-500 py-2">No other notes in the timeline.</p>
  );
}

function PinnedBlock(props: NoteListProps) {
  if (props.notes.length === 0) return null;
  return (
    <div className="space-y-2">
      <div
        className="flex items-center gap-1.5 text-xs font-bold
          text-gray-500 uppercase tracking-wider"
      >
        <Pin size={12} />
        Pinned
      </div>
      <div className="space-y-2 rounded-xl border border-amber-200 bg-amber-50/60 p-3">
        <NoteList {...props} />
      </div>
    </div>
  );
}

interface NotesBodyProps {
  pinned: PosNote[];
  timeline: PosNote[];
  listProps: Omit<NoteListProps, 'notes'>;
  isOnline: boolean;
  submitting: boolean;
  onAdd: (body: string, isPinned: boolean) => Promise<boolean>;
}

function NotesBody({
  pinned,
  timeline,
  listProps,
  isOnline,
  submitting,
  onAdd,
}: NotesBodyProps) {
  return (
    <div className="bg-gray-50 p-4 rounded-xl mt-6 space-y-5">
      <h3 className="font-bold text-gray-900">Notes</h3>
      <PinnedBlock notes={pinned} {...listProps} />
      <div className="space-y-2">
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">
          Timeline
        </p>
        {timeline.length === 0 ? (
          <TimelineEmpty hasPinned={pinned.length > 0} />
        ) : (
          <NoteList notes={timeline} {...listProps} />
        )}
      </div>
      <div className="border-t border-gray-200 pt-4">
        <AddNoteForm
          isOnline={isOnline}
          submitting={submitting}
          onSubmit={onAdd}
        />
      </div>
    </div>
  );
}

export const PosNotesSection: React.FC<PosNotesSectionProps> = ({ posId }) => {
  const { user, isTeamAdmin } = useAuth();
  const { isOnline } = useOnlineStatus();
  const notesState = usePosNotes(posId);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  if (notesState.loading) return <NotesLoading />;

  const pinned = notesState.notes.filter((n) => n.is_pinned);
  const timeline = notesState.notes.filter((n) => !n.is_pinned);
  const listProps = {
    userId: user?.id,
    isAdmin: isTeamAdmin,
    busyId: notesState.busyId,
    editingId: notesState.editingId,
    onStartEdit: (id: string) => notesState.setEditingId(id),
    onCancelEdit: () => notesState.setEditingId(null),
    onSaveEdit: (id: string, body: string, pin: boolean) => {
      void notesState.saveEdit(id, body, pin);
    },
    onDelete: (id: string) => setPendingDeleteId(id),
  };

  return (
    <>
      <NotesBody
        pinned={pinned}
        timeline={timeline}
        listProps={listProps}
        isOnline={isOnline}
        submitting={notesState.submitting}
        onAdd={notesState.addNote}
      />
      <ConfirmDialog
        open={pendingDeleteId !== null}
        onOpenChange={(open) => !open && setPendingDeleteId(null)}
        title="Delete note"
        message="This note will be removed from the timeline. This cannot be undone."
        confirmText="Delete"
        variant="danger"
        onConfirm={async () => {
          if (!pendingDeleteId) return;
          await notesState.deleteNote(pendingDeleteId);
          setPendingDeleteId(null);
        }}
      />
    </>
  );
};
